import React from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  DetailsList,
  IColumn,
  Separator,
  mergeStyles,
} from '@fluentui/react';

// Интерфейс для элементов таблицы документации
interface IDocument {
  key: string;
  fileName: string;
  filePath: string; // Путь к файлу в папке public
}

// Мок-данные для таблицы (файлы должны лежать в папке public/Documentation/...)
const documents: IDocument[] = [
  { 
    key: '1', 
    fileName: 'Описание архитектуры программного обеспечения', 
    filePath: '/Documentation/ОПИСАНИЕ АРХИТЕКТУРЫ ПРОГРАММНОГО ОБЕСПЕЧЕНИЯ.pdf' 
  },
  { 
    key: '2', 
    fileName: 'Описание процессов поддержания жизненного цикла программного обеспечения', 
    filePath: '/Documentation/ОПИСАНИЕ ПРОЦЕССОВ ПОДДЕРЖАНИЯ ЖИЗНЕННОГО ЦИКЛА ПРОГРАММНОГО ОБЕСПЕЧЕНИЯ.pdf' 
  },
  { 
    key: '3', 
    fileName: 'Описание функциональных характеристик программного обеспечения', 
    filePath: '/Documentation/ОПИСАНИЕ ФУНКЦИОНАЛЬНЫХ ХАРАКТЕРИСТИК ПРОГРАММНОГО ОБЕСПЕЧЕНИЯ.pdf' 
  },
  { 
    key: '4', 
    fileName: 'Свидетельство СметаFORTE', 
    filePath: '/Documentation/Свидетельство СметаFORTE.pdf' 
  },
  { 
    key: '5', 
    fileName: 'Справка о местоположении сервера от 14 июля 2026', 
    filePath: '/Documentation/Справка о местоположении сервера от 14 июля 2026.pdf' 
  },
  { 
    key: '6', 
    fileName: 'Тарифная политика', 
    filePath: '/Documentation/Тарифная политика.pdf' 
  },
];

const download: IDocument[] = [
  { 
    key: '1', 
    fileName: 'manifest.json', 
    filePath: '/Dists/manifest.json' 
  },
  { 
    key: '2', 
    fileName: 'SmetaFORTE.msi', 
    filePath: '/Dists/SmetaFORTE.msi' 
  },
  { 
    key: '3', 
    fileName: 'Инструкция по установке экземпляра программного обеспечения, предоставленного для проведения экспертной проверки для Единого реестра российского ПО ', 
    filePath: '/Documentation/Программный модуль СметаFORTE (инструкция).pdf' 
  },
];

const DocumentationPage: React.FC = () => {
  // Функция скачивания файла из папки public
  const handleDownload = (filePath: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = filePath;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Настройка колонок для DetailsList
  const columns: IColumn[] = [
    {
      key: 'fileName',
      name: 'Название файла',
      fieldName: 'fileName',
      minWidth: 250,
      maxWidth: 400,
      isResizable: true,
      onRender: (item: IDocument) => (
        <Text 
          styles={{ 
            root: { 
              whiteSpace: 'normal', 
              wordBreak: 'break-word', 
              lineHeight: '1.5',
              paddingRight: 10 
            } 
          }}
        >
          {item.fileName}
        </Text>
      ),
    },
    {
      key: 'action',
      name: 'Действие',
      fieldName: 'action',
      minWidth: 120,
      maxWidth: 150,
      onRender: (item: IDocument) => (
        <PrimaryButton
          text="Скачать"
          onClick={() => handleDownload(item.filePath, item.fileName)}
          styles={{ root: { minWidth: 110 } }}
        />
      ),
    },
  ];

  // Стили для переопределения фиксированной высоты строк в Fluent UI DetailsList
  const customListStyles = {
    root: {
      selectors: {
        '.ms-DetailsRow': {
          minHeight: 'auto !important',
          height: 'auto !important',
        },
        '.ms-DetailsRow-cell': {
          minHeight: 'auto !important',
          height: 'auto !important',
          paddingTop: 12,
          paddingBottom: 12,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
        '.ms-DetailsRow-fields': {
          alignItems: 'center', // Выравнивание кнопки по центру при переносе текста
        }
      }
    }
  };

  // Стили для карточек прайс-листа
  const priceCardStyles = mergeStyles({
    padding: 16,
    backgroundColor: '#f3f2f1', // Светло-серый фон Fluent UI
    borderRadius: 4,
    border: '1px solid #edebe9',
  });

  return (
    <Stack 
      tokens={{ childrenGap: 30 }} 
      styles={{ root: { maxWidth: 1000, margin: '0 auto', padding: 24 } }}
    >
      {/* 1. ТАБЛИЦА "ДОКУМЕНТАЦИЯ" */}
      <Stack tokens={{ childrenGap: 10 }}>
        <Text variant="xLarge" block styles={{ root: { fontWeight: 600, marginBottom: 8 } }}>
          Документация
        </Text>
        <DetailsList
          items={documents}
          columns={columns}
          styles={customListStyles}
          selectionMode={0} // Отключаем выделение строк
          compact={false}
        />
      </Stack>

      <Separator />

      {/* 2. ПРАЙС-ЛИСТ */}
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="xLarge" block styles={{ root: { fontWeight: 600 } }}>
          Тарифная политика
        </Text>
        
        <Stack tokens={{ childrenGap: 12 }}>
          <Stack horizontal verticalAlign="center" className={priceCardStyles} tokens={{ childrenGap: 10 }}>
            <Stack grow>
              <Text variant="mediumPlus">Тариф «Все включено» 6 мес</Text>
            </Stack>
            <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: '#0078d4' } }}>
              14 000 руб.
            </Text>
          </Stack>

          <Stack horizontal verticalAlign="center" className={priceCardStyles} tokens={{ childrenGap: 10 }}>
            <Stack grow>
              <Text variant="mediumPlus">Тариф «Все включено» 1 год</Text>
            </Stack>
            <Text variant="xLarge" styles={{ root: { fontWeight: 700, color: '#0078d4' } }}>
              24 500 руб.
            </Text>
          </Stack>
        </Stack>

        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 8 } }}>
          <Text variant="medium">
            Без НДС
          </Text>
          <Text variant="medium">
            Данное предложение не является публичной офертой.
          </Text>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>Условия оплаты:</Text>
          <Text variant="medium">
            Оплата производится по истечении срока подписки. Способы оплаты: банковский перевод или карта.
          </Text>
          
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600, marginTop: 8 } }}>Дополнительные услуги:</Text>
          <Text variant="medium">
            Техническая поддержка и обучение пользователей включены в стоимость подписки.
          </Text>
        </Stack>
      </Stack>

      <Separator />

      {/* 3. ОПИСАНИЕ ПРОГРАММЫ (5-7 предложений) */}
        <Stack tokens={{ childrenGap: 8 }} styles={{ root: { paddingLeft: 8 } }}>
          <Text variant="mediumPlus" styles={{ root: { fontWeight: 600 } }}>О программе «СметаFORTE»:</Text>
          <Text variant="medium">
            «СметаFORTE» — это современное программное обеспечение для автоматизации сметных расчетов, работающее по облачной модели SaaS. Приложение построено на надежной клиент-серверной веб-архитектуре с использованием передового стека технологий: React и TypeScript на фронтенде, Node.js и MongoDB на бэкенде. Для обеспечения высокой производительности, безопасности и масштабируемости применяется контейнеризация на базе Docker и веб-сервер Nginx. Все серверные мощности и базы данных надежно размещены на территории Российской Федерации в соответствии с требованиями законодательства. Пользователям доступен интуитивно понятный веб-интерфейс, который не требует установки дополнительного локального ПО и работает в любом современном браузере. В базовую стоимость подписки уже включены техническая поддержка и полноценное обучение работе с системой.
          </Text>
        </Stack>

      <Separator />
      
      {/* 4. ТАБЛИЦА "Загрузить экземпляр ПО" */}
      <Stack tokens={{ childrenGap: 10 }}>
        <Text variant="xLarge" block styles={{ root: { fontWeight: 600, marginBottom: 8 } }}>
          Загрузить экземпляр ПО
        </Text>
        <DetailsList
          items={download}
          columns={columns}
          styles={customListStyles}
          selectionMode={0} // Отключаем выделение строк
          compact={false}
        />
      </Stack>

    </Stack>
  );
};

export default DocumentationPage;